import { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link2, Copy, Check, X, ShieldCheck, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { priceLabel } from "@/components/YhModel3Provider";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR") : "—");
const CONSENT_TR = { pending: "Müşteri onayı bekleniyor", granted: "Onaylandı", revoked: "Geri alındı", superseded: "Geçersiz (yenilendi)" };
const CLAIM_TR = { pending: "Onay bekliyor", approved: "Onaylandı", rejected: "Reddedildi", closed_by_other: "Başkasına verildi", closed_consent: "Rıza nedeniyle kapandı", closed_cancelled: "İlan kapandı" };

const run = async (fn, msg, after) => {
  try { const r = await fn(); if (msg) toast.success(msg); after(); return r; }
  catch (e) { toast.error(formatErr(e.response?.data?.detail)); return null; }
};

const ConsentBox = ({ d, onChanged }) => {
  const [link, setLink] = useState(null);
  const phone = (d.yh_customer_snapshot?.phone || "").replace("+", "");
  const create = async () => {
    const r = await run(() => api.post(`/admin/transport/listings/${d.id}/consent-link`), "Onay bağlantısı oluşturuldu", onChanged);
    if (r) setLink(`${window.location.origin}/kvkk-onay/${r.data.token}`);
  };
  const copy = () => { navigator.clipboard?.writeText(link); toast.success("Bağlantı kopyalandı"); };
  const wa = link ? `https://wa.me/${phone}?text=${encodeURIComponent(`Merhaba, Yol Hattı araç taşıma talebiniz için iletişim bilgilerinizin hizmet sağlayıcılarla paylaşılmasına ilişkin onay sayfası: ${link}`)}` : "";
  const st = d.yh_consent_status;
  return (
    <div className="rounded-xl border p-3 space-y-2" data-testid="yh-m3-consent">
      <div className="flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="w-4 h-4" />KVKK müşteri onayı
        <Badge variant={st === "granted" ? "default" : "secondary"} data-testid="yh-m3-consent-status">{st === "granted" ? "Onay verildi · yayında" : st === "revoked" ? "Rıza geri alındı" : "Onay bekleniyor"}</Badge>
      </div>
      {d.status === "DRAFT" && (
        <div className="space-y-2">
          <div className="text-xs text-muted-foreground">İlan, müşteri bağlantıdaki metinleri okuyup açık rızasını kendisi işaretleyene kadar yayınlanmaz. Yeni bağlantı oluşturmak öncekini geçersiz kılar (72 saat geçerli).</div>
          <Button size="sm" variant="outline" onClick={create} data-testid="yh-m3-consent-create"><Link2 className="w-4 h-4 mr-1" />Onay bağlantısı oluştur</Button>
          {link && (
            <div className="rounded-lg bg-muted/50 p-2 text-xs break-all space-y-2" data-testid="yh-m3-consent-link">
              <div>{link}</div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={copy} data-testid="yh-m3-consent-copy"><Copy className="w-4 h-4 mr-1" />Kopyala</Button>
                <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="yh-m3-consent-wa" className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm"><MessageCircle className="w-4 h-4" />WhatsApp ile gönder</a>
              </div>
            </div>
          )}
        </div>
      )}
      {!!d.consents?.length && (
        <div className="text-xs space-y-1" data-testid="yh-m3-consent-history">
          <div className="font-semibold">Onay kayıtları</div>
          {d.consents.map((c) => (
            <div key={c.id} className="flex flex-wrap gap-x-2 text-muted-foreground">
              <span className="text-foreground">{CONSENT_TR[c.status] || c.status}</span>
              <span>metin v{c.text_version}</span><span>oluşturma {fmt(c.created_at)}</span>
              {c.granted_at && <span>onay {fmt(c.granted_at)} · IP {c.granted_ip}</span>}
              {c.revoked_at && <span>geri alma {fmt(c.revoked_at)}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Claims = ({ d, onChanged }) => {
  const claims = d.yh_claims || [];
  const decide = (c, action) => {
    if (action === "approve" && !window.confirm(`${c.business_name} için iş ataması onaylansın mı? Diğer başvurular kapanacak.`)) return;
    run(() => api.post(`/admin/transport/listings/${d.id}/claims/${c.id}/${action}`, { decision_note: "" }),
      action === "approve" ? "İş sağlayıcıya atandı" : "Başvuru reddedildi (24 saat yeniden başvuramaz)", onChanged);
  };
  return (
    <div className="space-y-2" data-testid="yh-m3-claims">
      <div className="font-semibold text-sm">"İşi Aldım" başvuruları ({claims.length})</div>
      {!claims.length && <div className="text-sm text-muted-foreground">Henüz başvuru yok.</div>}
      {claims.map((c) => (
        <div key={c.id} className="rounded-xl border p-3 flex items-center justify-between gap-2 flex-wrap" data-testid={`yh-m3-claim-${c.id}`}>
          <div className="text-sm"><b>{c.business_name}</b><div className="text-xs text-muted-foreground">{fmt(c.at)}</div></div>
          {c.status === "pending" && d.status === "OPEN" ? (
            <div className="flex gap-2">
              <Button size="sm" onClick={() => decide(c, "approve")} data-testid={`yh-m3-claim-approve-${c.id}`} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"><Check className="w-4 h-4 mr-1" />Onayla</Button>
              <Button size="sm" variant="outline" onClick={() => decide(c, "reject")} data-testid={`yh-m3-claim-reject-${c.id}`}><X className="w-4 h-4" /></Button>
            </div>
          ) : <Badge variant="secondary" data-testid={`yh-m3-claim-status-${c.id}`}>{CLAIM_TR[c.status] || c.status}</Badge>}
        </div>
      ))}
    </div>
  );
};

export const YhModel3Panel = ({ d, onChanged }) => (
  <div className="space-y-3" data-testid="yh-m3-panel">
    <div className="text-sm font-bold" data-testid="yh-m3-price">{priceLabel(d)}</div>
    <ConsentBox d={d} onChanged={onChanged} />
    <Claims d={d} onChanged={onChanged} />
  </div>
);
